import React, { useEffect, useState } from 'react';
import {
  X,
  Mail,
  Phone,
  Calendar,
  ArrowRight,
  Pencil,
  Trash2,
  Dog,
  RefreshCw,
} from 'lucide-react';
import { Customer, Booking, Communication } from '../types';
import { api } from '../services/api';
import {
  Avatar,
  Badge,
  Card,
  IconButton,
  SectionLabel,
  loyaltyTone,
} from './ui';

interface CustomerDetailProps {
  customer: Customer;
  onClose: () => void;
  onEditBooking: (booking: Booking) => void;
  onDeleteBooking: (id: string) => void;
}

const fmtDate = (dateStr: string, opts?: Intl.DateTimeFormatOptions) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  if (opts) return date.toLocaleDateString('en-GB', opts);
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
};

const fmtDateTime = (timestamp: string | null) => {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CustomerDetail: React.FC<CustomerDetailProps> = ({
  customer,
  onClose,
  onEditBooking,
  onDeleteBooking,
}) => (
  <div className="h-full flex flex-col bg-white">
    <div className="px-5 py-4 border-b border-sand-200 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <Avatar name={customer.name} size="lg" />
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-sand-900 truncate">
            {customer.name}
          </h3>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge tone={loyaltyTone(customer.loyaltyStatus)} uppercase>
              {customer.loyaltyStatus}
            </Badge>
            <span className="text-xs text-sand-500">
              £{Number(customer.totalSpent).toLocaleString('en-GB')} lifetime
            </span>
          </div>
        </div>
      </div>
      <IconButton label="Close" onClick={onClose}>
        <X className="h-4 w-4" />
      </IconButton>
    </div>

    <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-sand-50/50">
      <Card className="p-4">
        <SectionLabel className="mb-3">Contact</SectionLabel>
        <div className="space-y-2.5 text-sm">
          <div className="flex items-center gap-2.5 text-sand-800">
            <Mail className="h-4 w-4 text-sand-500 shrink-0" />
            <a
              href={`mailto:${customer.email}`}
              className="hover:text-sage-700 truncate"
            >
              {customer.email}
            </a>
          </div>
          {customer.phone && (
            <div className="flex items-center gap-2.5 text-sand-800">
              <Phone className="h-4 w-4 text-sand-500 shrink-0" />
              <a
                href={`tel:${customer.phone}`}
                className="hover:text-sage-700"
              >
                {customer.phone}
              </a>
            </div>
          )}
        </div>
      </Card>

      <BookingsSection
        customer={customer}
        onEditBooking={onEditBooking}
        onDeleteBooking={onDeleteBooking}
      />

      <EmailsSection customerId={customer.id} customerEmail={customer.email} />
    </div>
  </div>
);

const BookingsSection: React.FC<{
  customer: Customer;
  onEditBooking: (b: Booking) => void;
  onDeleteBooking: (id: string) => void;
}> = ({ customer, onEditBooking, onDeleteBooking }) => (
  <section>
    <div className="flex items-center justify-between mb-3 px-1">
      <SectionLabel>Booking history</SectionLabel>
      <Badge tone="sand">
        {customer.bookings.length}{' '}
        {customer.bookings.length === 1 ? 'booking' : 'bookings'}
      </Badge>
    </div>

    {customer.bookings.length === 0 ? (
      <Card className="p-6 text-center">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-sand-100 text-sand-500 mb-2">
          <Calendar className="h-4 w-4" />
        </div>
        <p className="text-sm text-sand-700">No bookings yet</p>
      </Card>
    ) : (
      <div className="space-y-3">
        {customer.bookings.map((b) => (
          <BookingCard
            key={b.id}
            booking={b}
            onEdit={() => onEditBooking(b)}
            onDelete={() => onDeleteBooking(b.id!)}
          />
        ))}
      </div>
    )}
  </section>
);

const BookingCard: React.FC<{
  booking: Booking;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ booking, onEdit, onDelete }) => (
  <Card className="overflow-hidden">
    <div className="px-4 py-3 bg-sand-50/60 border-b border-sand-100 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[10px] font-medium text-sand-500 uppercase tracking-wider">
          Stay
        </p>
        <p className="text-sm font-medium text-sand-900 flex items-center gap-1.5 mt-0.5">
          {fmtDate(booking.fromDate, { day: 'numeric', month: 'short' })}
          <ArrowRight className="h-3 w-3 text-sand-400" />
          {fmtDate(booking.toDate, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </p>
      </div>
      <div className="text-right">
        <p className="text-[10px] font-medium text-sand-500 uppercase tracking-wider">
          Net
        </p>
        <p className="text-base font-semibold text-sage-700">
          £{Number(booking.ourIncome || 0).toLocaleString('en-GB')}
        </p>
        <p className="text-[10px] text-sand-500 font-medium">
          Gross £{Number(booking.amountPaid).toLocaleString('en-GB')}
        </p>
      </div>
    </div>

    <div className="p-4 space-y-3">
      <div className="flex flex-wrap gap-1.5">
        <Badge tone="sand">
          {booking.adults}A · {booking.children}C · {booking.infants}I
        </Badge>
        {booking.dogs > 0 && (
          <Badge tone="sage">
            <Dog className="h-3 w-3 mr-1 inline" />
            {booking.dogs} {booking.dogs === 1 ? 'dog' : 'dogs'}
          </Badge>
        )}
        {booking.highChair && <Badge tone="terracotta">High chair</Badge>}
        {booking.cot && <Badge tone="terracotta">Cot</Badge>}
      </div>

      {booking.welcomeTrayContents && (
        <div className="bg-sand-50 rounded-xl p-3">
          <p className="text-[10px] font-medium text-sand-500 uppercase tracking-wider mb-1">
            Welcome tray
          </p>
          <p className="text-sm text-sand-700 leading-relaxed">
            {booking.welcomeTrayContents}
          </p>
        </div>
      )}

      <div className="pt-1 flex items-center justify-between">
        <span className="text-[11px] text-sand-500">
          Booked {fmtDate(booking.bookingDate)}
        </span>
        <div className="flex items-center gap-1">
          <IconButton label="Edit booking" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton label="Delete booking" variant="danger" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      </div>
    </div>
  </Card>
);

const EmailsSection: React.FC<{
  customerId: string;
  customerEmail: string;
}> = ({ customerId, customerEmail }) => {
  const [emails, setEmails] = useState<Communication[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getCustomerCommunications(customerId)
      .then((data) => {
        if (cancelled) return;
        setEmails(data || []);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Failed to load emails';
        setError(message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, reloadKey]);

  return (
    <section>
      <div className="flex items-center justify-between mb-3 px-1">
        <SectionLabel>Emails</SectionLabel>
        <div className="flex items-center gap-2">
          {emails && emails.length > 0 && (
            <Badge tone="sand">
              {emails.length} {emails.length === 1 ? 'email' : 'emails'}
            </Badge>
          )}
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading}
            aria-label="Refresh emails"
            className="text-sand-500 hover:text-sand-900 disabled:opacity-50 transition-colors"
          >
            <RefreshCw
              className={['h-3.5 w-3.5', loading ? 'animate-spin' : ''].join(' ')}
            />
          </button>
        </div>
      </div>

      {loading && (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="bg-white border border-sand-200 rounded-2xl p-4"
            >
              <div className="h-3 w-2/3 bg-sand-100 rounded animate-pulse mb-2" />
              <div className="h-3 w-1/2 bg-sand-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <Card className="p-4 border-red-200">
          <p className="text-sm text-red-700">{error}</p>
          <p className="text-xs text-sand-500 mt-1">
            Set Gmail env vars on the App Service to enable this.
          </p>
        </Card>
      )}

      {!loading && !error && emails && emails.length === 0 && (
        <Card className="p-6 text-center">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-sand-100 text-sand-500 mb-2">
            <Mail className="h-4 w-4" />
          </div>
          <p className="text-sm text-sand-700">No emails found</p>
          <p className="text-xs text-sand-500 mt-1">
            Last 12 months with {customerEmail}.
          </p>
        </Card>
      )}

      {!loading && !error && emails && emails.length > 0 && (
        <div className="space-y-2">
          {emails.map((m) => (
            <EmailCard key={m.id} email={m} />
          ))}
        </div>
      )}
    </section>
  );
};

const EmailCard: React.FC<{ email: Communication }> = ({ email }) => {
  const isInbound = email.direction === 'inbound';
  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 bg-sand-50/60 border-b border-sand-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={[
              'inline-flex items-center justify-center w-6 h-6 rounded-full',
              isInbound
                ? 'bg-sky-50 text-sky-700'
                : 'bg-sage-100 text-sage-700',
            ].join(' ')}
          >
            <Mail className="h-3 w-3" />
          </span>
          <Badge tone={isInbound ? 'sand' : 'blue'} uppercase>
            {email.direction}
          </Badge>
        </div>
        <span className="text-[11px] text-sand-500 shrink-0">
          {fmtDateTime(email.timestamp)}
        </span>
      </div>

      <div className="p-4 space-y-2">
        {email.subject && (
          <p className="text-sm font-medium text-sand-900 truncate">
            {email.subject}
          </p>
        )}
        {email.snippet && (
          <p className="text-xs text-sand-700 leading-relaxed line-clamp-3">
            {email.snippet}
          </p>
        )}
        <div className="pt-1 flex items-center justify-end">
          <a
            href={`https://mail.google.com/mail/u/0/#all/${email.threadId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium text-sage-700 hover:text-sage-900"
          >
            Open in Gmail
          </a>
        </div>
      </div>
    </Card>
  );
};

export default CustomerDetail;
